"use client";

import { motion } from "framer-motion";
import {
  ShieldCheck,
  Camera,
  Monitor,
  Clock3,
  LayoutGrid,
  Save,
} from "lucide-react";

import Section from "./ui/Section";
import ProductShowcase from "./showcase/ProductShowcase";

const features = [
  {
    icon: ShieldCheck,
    title: "Exam Integrity Tools",
    description:
      "Exam controls help support a more consistent and focused testing experience.",
  },
  {
    icon: Camera,
    title: "Camera Monitoring",
    description:
      "Camera-based monitoring can be used when enabled for an examination and supported by the device.",
  },
  {
    icon: Monitor,
    title: "Fullscreen Support",
    description:
      "A focused computer-based testing view helps reduce distractions during supported exams.",
  },
  {
    icon: Clock3,
    title: "Timed Assessments",
    description:
      "A visible countdown helps students manage their time throughout a timed test.",
  },
  {
    icon: LayoutGrid,
    title: "Question Navigation",
    description:
      "Use the question palette to move between questions and keep track of your progress.",
  },
  {
    icon: Save,
    title: "Response Saving",
    description:
      "Responses are saved during the exam flow to help protect progress if interruptions occur.",
  },
];

export default function ExamExperience() {
  return (
    <Section
      className="
        relative
        overflow-hidden
        bg-[#050B1A]
        py-20
        lg:py-28
      "
    >
      {/* Decorative brand accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-32 top-1/4 h-72 w-72 rounded-full bg-blue-500/[0.07] blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-amber-400/[0.06] blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div
        className="
          relative
          z-10
          grid
          items-center
          gap-12
          lg:grid-cols-2
          lg:gap-16
        "
      >
        {/* Exam interface preview */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="relative min-w-0"
        >
          <div
            className="pointer-events-none absolute -inset-5 rounded-[3rem] bg-brand-gold/[0.04] blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <ProductShowcase mode="exam" />
          </div>
        </motion.div>

        {/* Feature copy */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="min-w-0"
        >
          <div className="inline-flex items-center rounded-full border border-brand-gold/30 bg-brand-gold/[0.06] px-4 py-2">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand-gold">
              Exam Experience
            </span>
          </div>

          <h2 className="mt-7 max-w-2xl text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl xl:text-[3.5rem]">
            A focused space for{" "}
            <span className="text-brand-gold">better exam practice</span>
          </h2>

          <p className="mt-6 max-w-xl text-base leading-7 text-white/65 sm:text-lg sm:leading-8">
            Practice and take computer-based tests in an organised exam
            environment, with clear navigation, timed assessments, and
            exam controls where enabled.
          </p>

          <div className="mt-9 grid gap-3 sm:grid-cols-2">
            {features.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ y: -2 }}
                  className="
                    group
                    flex
                    min-w-0
                    gap-3
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.025]
                    p-4
                    transition-colors
                    duration-300
                    hover:border-brand-gold/25
                    hover:bg-white/[0.045]
                  "
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/[0.06] text-brand-gold transition-colors group-hover:border-brand-gold/45 group-hover:bg-brand-gold/10">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-sm font-bold tracking-tight text-white sm:text-base">
                      {feature.title}
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-white/55 sm:text-sm sm:leading-6">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
